// ─── ACCESS TOKEN SYSTEM ──────────────────────────────────────────────────────
// Shareable links for caregivers — no Google account required. Every link
// belongs to a caregiver (families/{id}/sitters/{caregiverId}); there's no
// anonymous/role-based link anymore, just that person's entitlement toggles.
// Tokens live in Firestore under accessTokens/{token}.
// The link embeds the token: https://yourapp.web.app/?t=TOKEN
// On return visits the token is read from localStorage.

import { fsSet, fsGet, fsGetCol } from './firebase';

const LOCAL_KEY = 'foh_access_token';

// ─── TOKEN SHAPE ─────────────────────────────────────────────────────────────
// {
//   token:       string (random 40-char hex),
//   familyId:    string,
//   caregiverId: string — sitters/{id} this link belongs to; also lets the
//                Session tab show that caregiver's own sessions.
//   label:       string — the caregiver's name, snapshotted at creation.
//   createdAt:   ISO string,
//   createdBy:   string  (owner email),
//   expiresAt:   ISO string | null  (null = never expires),
//   active:      boolean,
//   scopes:      { schedule, assignedEvents, kids, house, emergency,
//                  sessions: boolean } — which caregiver tabs the link unlocks.
// }

// Starting point for a newly-added caregiver's access — fully editable per
// person afterward, since there's no role to preset it from.
export function defaultScopes() {
  return { schedule: true, assignedEvents: false, kids: true, house: true, emergency: false, sessions: false };
}

// What each scope unlocks — shared by the per-caregiver access panel and (for
// tab id / label reference) SharedView.
export const SCOPE_META = [
  { id: 'schedule',       label: '📅 Schedule',          desc: 'Published days + pickups' },
  { id: 'assignedEvents', label: '🚗 My Assigned Events', desc: 'Just the pickups & events assigned to them, not the whole week' },
  { id: 'kids',           label: '🌱 Kids',              desc: 'Allergies, meds, routines' },
  { id: 'house',          label: '🪵 House',             desc: 'WiFi, safety, house info' },
  { id: 'emergency',      label: '🚨 Emergency',         desc: 'Contacts + services' },
  { id: 'sessions',       label: '🌤️ My Sessions',      desc: 'Their own logged sitting sessions' },
];

// ─── GENERATE ─────────────────────────────────────────────────────────────────

export function generateToken() {
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function createAccessToken({ familyId, caregiverId, label, createdBy, expiresInDays = 90, scopes = null }) {
  if (!familyId || !caregiverId) throw new Error('createAccessToken requires familyId and caregiverId — links always belong to a caregiver now');
  const token = generateToken();
  const now = new Date();
  const expiresAt = expiresInDays
    ? new Date(now.getTime() + expiresInDays * 86400000).toISOString()
    : null;

  const doc = {
    token,
    familyId,
    caregiverId,
    label:       label || 'Caregiver',
    createdAt:   now.toISOString(),
    createdBy,
    expiresAt,
    active:      true,
    scopes:      scopes || defaultScopes(),
  };

  await fsSet(`accessTokens/${token}`, doc);
  return doc;
}

// ─── VALIDATE ─────────────────────────────────────────────────────────────────

export async function validateToken(token) {
  if (!token) return null;
  try {
    const doc = await fsGet(`accessTokens/${token}`);
    if (!doc)          return null;
    if (!doc.active)   return null;
    if (doc.expiresAt && new Date(doc.expiresAt) < new Date()) return null;
    // Backfill scopes for tokens created before scoped links existed
    if (!doc.scopes) doc.scopes = defaultScopes();
    return doc;
  } catch {
    return null;
  }
}

// ─── LOCAL STORAGE ────────────────────────────────────────────────────────────

export function saveTokenLocally(token) {
  try { localStorage.setItem(LOCAL_KEY, token); } catch {}
}

export function getSavedToken() {
  try { return localStorage.getItem(LOCAL_KEY) || null; } catch { return null; }
}

export function clearSavedToken() {
  try { localStorage.removeItem(LOCAL_KEY); } catch {}
}

// ─── REVOKE ───────────────────────────────────────────────────────────────────

export async function revokeToken(token) {
  await fsSet(`accessTokens/${token}`, { active: false });
}

// ─── LIST (for settings management) ──────────────────────────────────────────

export async function listTokensForFamily(familyId) {
  try {
    const all = await fsGetCol('accessTokens', [
      { field: 'familyId', op: '==', value: familyId },
      { field: 'active',   op: '==', value: true },
    ]);
    return (all || []).sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || '')
    );
  } catch {
    return [];
  }
}

// ─── BUILD LINK ───────────────────────────────────────────────────────────────

export function buildShareLink(token) {
  const base = window.location.origin + window.location.pathname;
  return `${base}?t=${token}`;
}

// ─── EXTRACT TOKEN FROM URL ───────────────────────────────────────────────────

export function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('t') || null;
}

// ─── CLEAN TOKEN FROM URL (after reading it) ──────────────────────────────────

export function cleanTokenFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('t');
  window.history.replaceState({}, '', url.toString());
}

// ─── GOOGLE SIGN-IN EMAIL INDEX ────────────────────────────────────────────────
// families/{id}.signInEmails is a flat array of emails currently enabled for
// caregiver Google sign-in, kept in sync with each caregiver's email +
// access.googleSignInEnabled toggle. It exists so App.jsx can find the right
// family with a plain array-contains query at sign-in time, before any
// per-family data (including the sitters subcollection) has loaded — which
// caregiver/scopes it maps to is then resolved locally from that family's
// already-loaded sitters list, not from this array.
export async function syncSignInEmail({ familyId, email, previousEmail, enabled, familyData, setFamilyData }) {
  const current = new Set((familyData?.signInEmails || []).map(e => e.toLowerCase()));
  const cleanEmail = email?.trim().toLowerCase();
  const cleanPrev  = previousEmail?.trim().toLowerCase();

  if (cleanPrev && cleanPrev !== cleanEmail) current.delete(cleanPrev);
  if (enabled && cleanEmail) current.add(cleanEmail);
  else if (cleanEmail) current.delete(cleanEmail);

  const updated = Array.from(current);
  await fsSet(`families/${familyId}`, { signInEmails: updated });
  setFamilyData?.(prev => ({ ...prev, signInEmails: updated }));
}

// ─── HOMESCHOOL STUDENT EMAIL INDEX ────────────────────────────────────────────
// families/{id}.homeschoolStudentEmails is a flat array of emails currently
// enabled for homeschool-student self-service (their own assignment/
// attendance writes in the school module), kept in sync with each child's
// schoolType + studentEmail fields — same pattern as syncSignInEmail above,
// used the same way: App.jsx's loadUserAndFamily does a plain array-contains
// query against it at sign-in time, before the children subcollection loads.
export async function syncHomeschoolStudent({ familyId, email, previousEmail, enabled, familyData, setFamilyData }) {
  const current = new Set((familyData?.homeschoolStudentEmails || []).map(e => e.toLowerCase()));
  const cleanEmail = email?.trim().toLowerCase();
  const cleanPrev  = previousEmail?.trim().toLowerCase();

  if (cleanPrev && cleanPrev !== cleanEmail) current.delete(cleanPrev);
  if (enabled && cleanEmail) current.add(cleanEmail);
  else if (cleanEmail) current.delete(cleanEmail);

  const updated = Array.from(current);
  await fsSet(`families/${familyId}`, { homeschoolStudentEmails: updated });
  setFamilyData?.(prev => ({ ...prev, homeschoolStudentEmails: updated }));
}
